import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, get, post } from '../api/client.js';
import { BELL_SEQUENCE_TRACKS, trackCode, trackLabel } from '../lib/tracks.js';
import { CommandProgress, type TrackedCommand } from './CommandProgress.js';
import { Field, Modal, Notice } from './ui.js';

/** Longest take: a spoken announcement, not a programme. */
const MAX_RECORD_MS = 5 * 60_000;
/** The device format, so what is previewed here is what the bell plays. */
const SAMPLE_RATE = 22_050;

const CATEGORIES = [
  { value: 'announcement', label: 'إعلان' },
  { value: 'bell', label: 'جرس' },
  { value: 'break', label: 'استراحة' },
  { value: 'anthem', label: 'نشيد' },
  { value: 'emergency', label: 'طوارئ' },
];

type Phase = 'idle' | 'asking' | 'recording' | 'processing' | 'review' | 'saving' | 'done';

/** Keep it on the bells' cards under a number, or play it once on one bell and keep nothing there. */
type Publish = 'store' | 'once';

interface Take {
  wav: Blob;
  url: string;
  durationMs: number;
}

interface Saved {
  id: string;
  name: string;
  track_number: number | null;
}

interface AssetStatus {
  id: string;
  state: string;
  failure_reason: string | null;
  track_number: number | null;
}

interface DeviceRow {
  id: string;
  name: string;
  online: boolean;
  board_profile?: string;
  school: { name: string };
}

/* ------------------------------------------------------------ audio helpers */

/** Leading and trailing silence cut (with a little air), peaks brought up to −1 dBFS, click-free edges. */
function polishTake(input: Float32Array): Float32Array {
  const threshold = 0.02;
  const pad = Math.round(SAMPLE_RATE * 0.15);
  let first = 0;
  while (first < input.length && Math.abs(input[first]!) < threshold) first++;
  let last = input.length - 1;
  while (last > first && Math.abs(input[last]!) < threshold) last--;
  if (first >= last) return input;

  const out = input.slice(Math.max(0, first - pad), Math.min(input.length, last + pad + 1));
  let peak = 0;
  for (let i = 0; i < out.length; i++) peak = Math.max(peak, Math.abs(out[i]!));
  if (peak > 0 && peak < 0.89) {
    const gain = Math.min(0.89 / peak, 8);
    for (let i = 0; i < out.length; i++) out[i] = out[i]! * gain;
  }
  const fade = Math.min(Math.round(SAMPLE_RATE * 0.01), Math.floor(out.length / 2));
  for (let i = 0; i < fade; i++) {
    const g = i / fade;
    out[i] = out[i]! * g;
    out[out.length - 1 - i] = out[out.length - 1 - i]! * g;
  }
  return out;
}

function encodeWav(samples: Float32Array, rate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buffer);
  const text = (offset: number, s: string): void => {
    for (let i = 0; i < s.length; i++) v.setUint8(offset + i, s.charCodeAt(i));
  };
  text(0, 'RIFF');
  v.setUint32(4, 36 + samples.length * 2, true);
  text(8, 'WAVE');
  text(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  text(36, 'data');
  v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!));
    v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Browsers record WebM/Opus or MP4/AAC, which the library does not accept, so
 * the take is decoded and rendered here to mono 16-bit WAV at the device rate.
 */
async function encodeTake(recorded: Blob, polish: boolean): Promise<{ take: Take; peak: number }> {
  const Ctx =
    window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(await recorded.arrayBuffer());
  } finally {
    void ctx.close();
  }

  const frames = Math.max(1, Math.ceil(decoded.duration * SAMPLE_RATE));
  const offline = new OfflineAudioContext(1, frames, SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();

  let samples = rendered.getChannelData(0);
  let peak = 0;
  for (let i = 0; i < samples.length; i++) peak = Math.max(peak, Math.abs(samples[i]!));
  if (polish) samples = polishTake(samples);

  const wav = encodeWav(samples, SAMPLE_RATE);
  return {
    take: { wav, url: URL.createObjectURL(wav), durationMs: Math.round((samples.length / SAMPLE_RATE) * 1000) },
    peak,
  };
}

function clock(ms: number): string {
  const total = Math.floor(ms / 1000);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function defaultName(): string {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `تسجيل ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}.${pad(now.getMinutes())}`;
}

/* ---------------------------------------------------------------- component */

/**
 * Record an announcement from the microphone, then either keep it (saved to
 * the audio library with a bell track number, which puts it on the bells'
 * cards) or broadcast it once on one bell without keeping it on its card.
 */
export function AudioRecorder({
  onClose,
  onSaved,
  canPlay,
}: {
  onClose: () => void;
  onSaved: () => void;
  canPlay: boolean;
}): JSX.Element {
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [take, setTake] = useState<Take | null>(null);
  const [polish, setPolish] = useState(true);
  const [name, setName] = useState(defaultName);
  const [category, setCategory] = useState('announcement');
  const [track, setTrack] = useState('auto');
  const [publish, setPublish] = useState<Publish>('store');
  const [deviceId, setDeviceId] = useState('');
  const [saved, setSaved] = useState<Saved | null>(null);
  const [tracked, setTracked] = useState<TrackedCommand | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const rawRef = useRef<Blob | null>(null);
  const meterCtxRef = useRef<AudioContext | null>(null);
  const meterRef = useRef<HTMLSpanElement>(null);
  const rafRef = useRef<number | null>(null);
  const startedRef = useRef(0);
  const mountedRef = useRef(true);
  const takeUrlRef = useRef<string | null>(null);
  const onceSentRef = useRef(false);
  const closeRef = useRef<() => void>(onClose);
  const handleClose = useCallback(() => closeRef.current(), []);

  const tracks = useQuery({
    queryKey: ['audio', 'tracks'],
    queryFn: () => get<{ next_free: number | null }>('/audio/tracks'),
  });

  const devices = useQuery({
    queryKey: ['devices', 'recorder'],
    queryFn: () => get<{ items: DeviceRow[] }>('/devices?page_size=200&status=ACTIVE'),
    enabled: canPlay,
  });
  const bells = (devices.data?.items ?? []).filter((d) => d.board_profile === 'esp32-dfplayer-bell');

  const status = useQuery({
    queryKey: ['audio', 'recorded', saved?.id],
    queryFn: () => get<AssetStatus>(`/audio/${saved!.id}`),
    enabled: Boolean(saved),
    refetchInterval: (q) => {
      const s = (q.state.data as AssetStatus | undefined)?.state;
      return s === 'READY' || s === 'FAILED' ? false : 1500;
    },
  });

  const releaseMic = (): void => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void meterCtxRef.current?.close().catch(() => undefined);
    meterCtxRef.current = null;
  };

  const replaceTake = (next: Take | null): void => {
    if (takeUrlRef.current) URL.revokeObjectURL(takeUrlRef.current);
    takeUrlRef.current = next?.url ?? null;
    setTake(next);
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      releaseMic();
      if (takeUrlRef.current) URL.revokeObjectURL(takeUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (phase !== 'recording') return;
    const t = setInterval(() => {
      const ms = Date.now() - startedRef.current;
      setElapsed(ms);
      if (ms >= MAX_RECORD_MS) stop();
    }, 200);
    return () => clearInterval(t);
  }, [phase]);

  // A one-time broadcast goes out as soon as the recording is in the device format.
  const readyState = status.data?.state;
  useEffect(() => {
    if (publish !== 'once' || !saved || onceSentRef.current || readyState !== 'READY') return;
    onceSentRef.current = true;
    const bell = bells.find((d) => d.id === deviceId);
    post<{ command_id: string; expires_at: string | null }>(`/devices/${deviceId}/commands`, {
      type: 'play_audio',
      asset_id: saved.id,
      mode: 'once',
    })
      .then((res) => {
        if (!mountedRef.current) return;
        setTracked({
          deviceId,
          commandId: res.command_id,
          deviceName: bell?.name ?? '',
          action: `بث «${saved.name}» لمرة واحدة`,
          expiresAt: res.expires_at,
        });
      })
      .catch((err: Error) => {
        if (mountedRef.current) setError(err.message);
      });
  }, [publish, saved, readyState, deviceId, bells]);

  const process = async (recorded: Blob, withPolish: boolean): Promise<void> => {
    setPhase('processing');
    try {
      const { take: next, peak } = await encodeTake(recorded, withPolish);
      if (!mountedRef.current) return;
      if (peak < 0.01) {
        setError('لم يُلتقط أي صوت. تأكد أن المايك الصحيح مختار وغير مكتوم، ثم أعد التسجيل.');
        replaceTake(null);
        setPhase('idle');
        return;
      }
      if (next.durationMs < 300) {
        setError('التسجيل قصير جدًا. أعد التسجيل.');
        replaceTake(null);
        setPhase('idle');
        return;
      }
      replaceTake(next);
      setPhase('review');
    } catch {
      if (!mountedRef.current) return;
      setError('تعذرت معالجة التسجيل في المتصفح. جرّب متصفحًا آخر (Chrome أو Edge أو Safari حديث).');
      setPhase('idle');
    }
  };

  const start = async (): Promise<void> => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('متصفحك لا يدعم التسجيل من المايك. استخدم Chrome أو Edge أو Safari حديثًا.');
      return;
    }
    setPhase('asking');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (err) {
      const e = err as DOMException;
      setPhase('idle');
      setError(
        e.name === 'NotAllowedError'
          ? 'رُفض الوصول إلى المايك. اسمح لهذا الموقع باستخدام المايك من شريط العنوان في المتصفح، ثم أعد المحاولة.'
          : e.name === 'NotFoundError'
            ? 'لم يُعثر على مايك متصل بهذا الجهاز.'
            : `تعذر فتح المايك: ${e.message}`,
      );
      return;
    }
    if (!mountedRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    streamRef.current = stream;

    // Level meter, drawn straight to the DOM so it does not re-render the dialog 60 times a second.
    try {
      const ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      meterCtxRef.current = ctx;
      const buf = new Float32Array(analyser.fftSize);
      const draw = (): void => {
        analyser.getFloatTimeDomainData(buf);
        let peak = 0;
        for (let i = 0; i < buf.length; i++) peak = Math.max(peak, Math.abs(buf[i]!));
        if (meterRef.current) {
          meterRef.current.style.width = `${Math.min(100, Math.round(Math.sqrt(peak) * 100))}%`;
          meterRef.current.dataset.clip = peak > 0.98 ? 'true' : 'false';
        }
        rafRef.current = requestAnimationFrame(draw);
      };
      draw();
    } catch {
      /* the meter is a nicety; recording works without it */
    }

    const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'].find((t) =>
      MediaRecorder.isTypeSupported(t),
    );
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      releaseMic();
      if (!mountedRef.current) return;
      const recorded = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
      rawRef.current = recorded;
      void process(recorded, polish);
    };
    recorder.start(250);
    recorderRef.current = recorder;
    startedRef.current = Date.now();
    setElapsed(0);
    replaceTake(null);
    setPhase('recording');
  };

  function stop(): void {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  }

  const togglePolish = (next: boolean): void => {
    setPolish(next);
    if (rawRef.current && phase === 'review') void process(rawRef.current, next);
  };

  const save = async (): Promise<void> => {
    if (!take) return;
    const once = publish === 'once';
    if (once && !deviceId) {
      setError('اختر جهاز الجرس الذي سيبث التسجيل.');
      return;
    }
    setPhase('saving');
    setError(null);
    const clean = (name.trim() || defaultName()).replace(/[\\/:*?"<>|]+/g, ' ').slice(0, 180);
    const form = new FormData();
    // Fields before the file, or the server does not see them.
    form.append('category', once ? 'broadcast' : category);
    form.append('track', once ? 'none' : track);
    form.append('file', take.wav, `${clean}.wav`);
    try {
      const res = await api<{ id: string; track_number: number | null }>('/audio', { method: 'POST', body: form });
      if (!mountedRef.current) return;
      onceSentRef.current = false;
      setSaved({ id: res.id, name: clean, track_number: res.track_number });
      setPhase('done');
      onSaved();
    } catch (err) {
      if (!mountedRef.current) return;
      setError((err as Error).message);
      setPhase('review');
    }
  };

  const reset = (): void => {
    replaceTake(null);
    rawRef.current = null;
    onceSentRef.current = false;
    setSaved(null);
    setTracked(null);
    setName(defaultName());
    setError(null);
    setElapsed(0);
    setPhase('idle');
  };

  const nextFree = tracks.data?.next_free ?? null;
  const asset = status.data;

  let footer: JSX.Element;
  if (phase === 'recording') {
    footer = (
      <button className="danger" onClick={stop}>
        ■ إيقاف التسجيل
      </button>
    );
  } else if (phase === 'review' || phase === 'saving') {
    footer = (
      <>
        <button onClick={onClose} disabled={phase === 'saving'}>
          إلغاء
        </button>
        <button onClick={() => void start()} disabled={phase === 'saving'}>
          إعادة التسجيل
        </button>
        <button
          className="primary"
          onClick={() => void save()}
          disabled={phase === 'saving' || !take || (publish === 'once' && !deviceId)}
        >
          {phase === 'saving' ? 'جارٍ الإرسال…' : publish === 'once' ? 'بث الآن' : 'حفظ ونشر'}
        </button>
      </>
    );
  } else if (phase === 'done') {
    footer = (
      <>
        <button onClick={reset}>تسجيل جديد</button>
        <button className="primary" onClick={onClose}>
          تم
        </button>
      </>
    );
  } else {
    footer = (
      <>
        <button onClick={onClose}>إلغاء</button>
        <button className="primary" onClick={() => void start()} disabled={phase !== 'idle'}>
          {phase === 'asking' ? 'بانتظار إذن المايك…' : phase === 'processing' ? 'جارٍ التجهيز…' : '● ابدأ التسجيل'}
        </button>
      </>
    );
  }

  // Modal re-runs its focus effect whenever onClose changes, so it gets one stable callback.
  // Closing while recording (Esc, backdrop) stops the take instead of discarding it.
  closeRef.current = () => (recorderRef.current?.state === 'recording' ? stop() : onClose());

  return (
    <Modal title="تسجيل من المايك" onClose={handleClose} footer={footer}>
      {error ? <Notice tone="warn">{error}</Notice> : null}

      {phase === 'idle' || phase === 'asking' ? (
        <p style={{ color: 'var(--text-soft)', marginTop: 0 }}>
          سجّل إعلانًا بصوتك واستمع إليه، ثم احفظه على بطاقات أجهزة الجرس برقم، أو ابثّه مرة واحدة على سماعات
          مدرسة دون حفظه. أقصى مدة {MAX_RECORD_MS / 60_000} دقائق. سيطلب المتصفح الإذن باستخدام المايك.
        </p>
      ) : null}

      {phase === 'recording' ? (
        <div className="rec-live">
          <div className="rec-time num" dir="ltr">
            <span className="rec-dot" aria-hidden="true" /> {clock(elapsed)}
          </div>
          <div className="rec-meter" aria-hidden="true">
            <span ref={meterRef} />
          </div>
          <div className="hint">
            تكلّم بوضوح على بُعد شبر من المايك. يتوقف التسجيل تلقائيًا بعد {MAX_RECORD_MS / 60_000} دقائق.
          </div>
        </div>
      ) : null}

      {phase === 'processing' ? <p style={{ color: 'var(--text-soft)' }}>جارٍ تجهيز التسجيل…</p> : null}

      {(phase === 'review' || phase === 'saving') && take ? (
        <>
          <audio controls src={take.url} style={{ width: '100%', margin: '4px 0 8px' }}>
            متصفحك لا يدعم تشغيل الصوت.
          </audio>
          <div className="hint" style={{ marginBottom: 12 }}>
            المدة <span className="num">{clock(take.durationMs)}</span> — هذا ما سيسمعه الطلاب بجودة جهاز الجرس.
          </div>
          <label className="row" style={{ gap: 8, marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={polish}
              onChange={(e) => togglePolish(e.target.checked)}
              style={{ width: 'auto' }}
            />
            قص الصمت في البداية والنهاية ورفع مستوى الصوت
          </label>

          <Field label="ماذا أفعل بالتسجيل؟">
            <div style={{ display: 'grid', gap: 6 }}>
              <label className="row" style={{ gap: 8 }}>
                <input
                  type="radio"
                  name="recorder-publish"
                  checked={publish === 'store'}
                  onChange={() => setPublish('store')}
                  style={{ width: 'auto' }}
                />
                حفظ دائم: في مكتبة الأصوات وعلى بطاقة أجهزة الجرس برقم
              </label>
              <label className="row" style={{ gap: 8, opacity: canPlay ? 1 : 0.55 }}>
                <input
                  type="radio"
                  name="recorder-publish"
                  checked={publish === 'once'}
                  disabled={!canPlay}
                  onChange={() => setPublish('once')}
                  style={{ width: 'auto' }}
                />
                بث مباشر لمرة واحدة على سماعات مدرسة، دون حفظه على بطاقة الجهاز
                {!canPlay ? ' (يتطلب صلاحية التشغيل اليدوي)' : ''}
              </label>
            </div>
          </Field>

          <Field label="اسم التسجيل">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={180} />
          </Field>

          {publish === 'once' ? (
            <>
              <Field label="جهاز الجرس" hint="يحتاج جهازًا ببطاقة ذاكرة ومخرج صوت (النسخة 3)">
                {bells.length === 0 ? (
                  <div className="hint">لا توجد أجهزة جرس مفعّلة.</div>
                ) : (
                  <select value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
                    <option value="">اختر الجهاز</option>
                    {bells.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.school.name} — {d.name} ({d.online ? 'متصل' : 'غير متصل'})
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Notice tone="warn">سيُسمع التسجيل على سماعات المدرسة المختارة فور وصوله إلى الجهاز.</Notice>
            </>
          ) : (
            <>
              <div className="grid cols-2">
                <Field label="النوع">
                  <select value={category} onChange={(e) => setCategory(e.target.value)}>
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="رقم الجرس" hint="الرقم هو ما ينشر التسجيل على أجهزة الجرس">
                  <select value={track} onChange={(e) => setTrack(e.target.value)}>
                    <option value="auto">تلقائي{nextFree ? ` (${trackCode(nextFree)})` : ''}</option>
                    <optgroup label="استبدال صوت في تسلسل الجرس">
                      {Array.from({ length: BELL_SEQUENCE_TRACKS }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>
                          {trackLabel(n)}
                        </option>
                      ))}
                    </optgroup>
                    <option value="none">بدون رقم (يُحفظ في المكتبة فقط)</option>
                  </select>
                </Field>
              </div>
              {track !== 'auto' && track !== 'none' ? (
                <Notice tone="warn">
                  سيحل هذا التسجيل محل «{trackLabel(Number(track))}» على كل أجهزة الجرس.
                </Notice>
              ) : null}
            </>
          )}
        </>
      ) : null}

      {phase === 'done' && saved && publish === 'once' ? (
        <div>
          {!asset || asset.state === 'UPLOADED' || asset.state === 'TRANSCODING' ? (
            <Notice tone="info">⏳ جارٍ تجهيز التسجيل لصيغة الجهاز قبل بثه…</Notice>
          ) : asset.state === 'FAILED' ? (
            <Notice tone="danger">تعذر تجهيز التسجيل: {asset.failure_reason ?? 'خطأ غير معروف'}</Notice>
          ) : tracked ? (
            <CommandProgress key={tracked.commandId} command={tracked} />
          ) : error ? null : (
            <Notice tone="info">⏳ جارٍ إرسال التسجيل إلى الجهاز…</Notice>
          )}
          <div className="hint" style={{ marginTop: 8 }}>
            لا يبقى على بطاقة الجهاز بعد بثه. تبقى نسخة في «مكتبة الأصوات» للرجوع إليها، ويمكنك أرشفتها.
          </div>
        </div>
      ) : null}

      {phase === 'done' && saved && publish === 'store' ? (
        <div>
          {!asset || asset.state === 'UPLOADED' || asset.state === 'TRANSCODING' ? (
            <Notice tone="info">حُفظ «{saved.name}». جارٍ تجهيزه لصيغة الجهاز…</Notice>
          ) : asset.state === 'FAILED' ? (
            <Notice tone="danger">تعذر تجهيز التسجيل: {asset.failure_reason ?? 'خطأ غير معروف'}</Notice>
          ) : asset.track_number !== null ? (
            <Notice tone="info">
              <strong>
                نُشر «{saved.name}» برقم الجرس {trackCode(asset.track_number)}.
              </strong>
              <br />
              يصل تلقائيًا إلى أجهزة الجرس ذات بطاقة الذاكرة (النسخة 3) خلال دقائق. لأجهزة النسخة 2: «تنزيل
              للبطاقة» وانسخه إلى مجلد mp3.
            </Notice>
          ) : (
            <Notice tone="info">حُفظ «{saved.name}» في مكتبة الأصوات بدون رقم جرس، فلن يصل إلى أجهزة الجرس.</Notice>
          )}
          {asset?.state === 'READY' ? (
            <div className="row" style={{ marginTop: 8 }}>
              {asset.track_number !== null && canPlay ? (
                <Link to={`/playback?asset=${saved.id}`}>تشغيله الآن في مدرسة ←</Link>
              ) : null}
              {asset.track_number !== null ? (
                <a href={`/api/v1/audio/${saved.id}/card-file`} download>
                  تنزيل للبطاقة
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
